import React, { useMemo, useCallback } from "react";

import { useDrop, useDrag } from "react-dnd";
import SearchableSelect from "util/component/SearchableSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

const UnitRow = ({
    data,
    index,
    moveRow = () => {},
    deleteRow = () => {},
    changeRow = () => {},
    callDetail = () => {}
}) => {
    const _Ref = React.useRef(null);
    const [, drop] = useDrop({
        type: "row",
        accept: "row",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!_Ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = _Ref.current.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
            // Time to actually perform the action
            // moveRow(code, dragIndex, hoverIndex)
            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            moveRow(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [, drag] = useDrag({
        type: "row",
        item: () => {
            return { index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(drop(_Ref));

    const Row = useMemo(() => {
        const tempRow = { ...data };
        const tempValue = tempRow?.templateRevision + (tempRow?.templateTemp === "Y" ? "T" : "");
        const options = new Array(tempRow?.maxRevision).fill().map((e, i) => {
            return { label: i + 1, value: i + 1 };
        });

        if (tempRow.maxTempYn === "Y") {
            options.push({
                label: tempRow.maxRevision,
                value: tempRow.maxRevision + "T",
                temp: "Y",
            });
        }
        let isWarn = "";
        if (tempRow?.maxTempYn === "Y") {
            if (tempRow?.templateTemp !== "Y") isWarn = "low-revision-warn";
        } else {
            if (tempRow?.maxRevision > tempRow?.templateRevision) isWarn = "low-revision-warn";
        }
        
        return (
            <tr ref={_Ref}>
                <td className={"text-center"}>{data.projectNm}</td>
                <td className={"text-center"}>{data.templateNm}</td>
                <td className={"text-center"}>{data.processCd}</td>
                <td title={data.description}>{data.description}</td>

                <td
                    className={
                        "text-center " + isWarn
                    }
                    style={{ overflow: "initial" }}
                >
                    <SearchableSelect
                        options={options}
                        value={tempValue}
                        onChange={changeRow}
                        revision={true}
                    />
                </td>
                <td
                    className={"text-center"}
                    style={{ cursor: "pointer", color: "red" }}
                    onClick={(e) => deleteRow(e, index)}
                >
                    {"X"}
                </td>
                <td
                    className={"text-center"}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => callDetail()}
                >
                    {"->"}
                </td>
            </tr>
        );
    }, [changeRow, data, deleteRow, index]);

    return Row;
};

const UnitRow2 = ({ data, onClick = () => {}, index }) => {
    const style = data.selected ? { backgroundColor: "var(--main-color)", color: "#FFF" } : {};
    const RevisionRender = useMemo(() => {
        if(data.tempYn === "Y") {
            return <>{data.revision}<sup className="temp">Temp</sup></>
        } else {
            return <>{data.revision}</>
        }
        
    },[data])
    return (
        <tr style={{ ...style, cursor: "pointer" }} data-index={index} onClick={onClick}>
            <td className={"text-center"} style={{ textOverflow: "initial", padding: 0 }}>
                {data.selected && <FontAwesomeIcon icon={faCheck} />}
            </td>
            <td className={"text-center"}>{data.projectNm}</td>
            <td className={"text-center"}>{data.mbrNo}</td>
            <td className={"text-center"}>{data.processCd}</td>
            <td title={data.description}>{data.description}</td>
            <td className={"text-center"}>{RevisionRender}</td>
        </tr>
    );
};

const DropBox = ({ children, addRow, states }) => {
    const [{ canDrop, isOver }, drop] = useDrop(
        () => ({
            accept: "box",
            drop: (item) => {
                addRow(item);
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
            }),
        }),
        [states]
    );

    const isActive = canDrop && isOver;
    let style = { position: "sticky", top: 0 };
    // if (isActive) {
    //     style = { ...style, backgroundColor: "#81c6e6" };
    // }
    if (canDrop) {
        style = { ...style, backgroundColor: "#81c6e6" };
    }
    return (
        <div data-testid="dustbin">
            <div style={style} ref={drop}>
                {children}
            </div>
        </div>
    );
};

const DropBox2 = ({ children }) => {
    let style = { position: "sticky", top: 0 };
    return (
        <div data-testid="dustbin">
            <div style={style}>{children}</div>
        </div>
    );
};

const Rows = {
    UnitRow,
    UnitRow2,
    DropBox,
    DropBox2,
};

export default Rows;
export { UnitRow, UnitRow2, DropBox, DropBox2 };
